import { UnauthorizedException } from '@/domain/authenticating/authorization/exception/UnauthorizedException'
import { ssoCookieName } from '@/domain/authenticating/authorization/repository/AuthorizationRepository'
import type { SsoCookie } from '@/infrastructure/api/http/middleware/RetryRefreshToken'
import Cookies from 'js-cookie'

export const getCookie = <T>(name: string): T | undefined => {
  const cookie = Cookies.get(name)
  if (!cookie) {
    return
  }

  try {
    return JSON.parse(cookie) as T
  } catch {
    // A malformed cookie must be treated as absent instead of crashing the caller
    return
  }
}

export const getSsoCookie = (): SsoCookie | undefined => {
  const cookie = getCookie<SsoCookie>(ssoCookieName)

  if (!cookie?._token || !cookie?._refreshToken) {
    return
  }

  return cookie
}

export const hasSsoTokens = (): boolean => !!getSsoCookie()

export const getSsoCookieOrThrow = (): SsoCookie => {
  const cookie = getSsoCookie()

  if (!cookie) {
    throw new UnauthorizedException('No tokens found.')
  }

  return cookie
}

export type WaitForSsoCookieOptions = {
  retries?: number
  initialDelayMs?: number
  backoffFactor?: number
}

export const waitForSsoCookie = async (
  { retries = 5, initialDelayMs = 100, backoffFactor = 2 }: WaitForSsoCookieOptions = {},
): Promise<SsoCookie | undefined> => {
  let cookie = getSsoCookie()
  let delay = initialDelayMs

  for (let attempt = 0; !cookie && attempt < retries; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, delay))
    delay *= backoffFactor
    cookie = getSsoCookie()
  }

  return cookie
}

